import { createContext, useContext, useEffect } from 'react'

export type DiscoverySearchConfig = {
  placeholder: string
  ariaLabel: string
}

export type DiscoverySearchContextValue = {
  query: string
  setQuery: (value: string) => void
  config: DiscoverySearchConfig | null
  registerSearch: (config: DiscoverySearchConfig) => void
  unregisterSearch: () => void
}

export const DiscoverySearchContext = createContext<DiscoverySearchContextValue | null>(null)

const noop = () => {}

/** Header-side accessor for the shared search state (used by the header search row). */
export function useDiscoverySearchControls() {
  return useContext(DiscoverySearchContext)
}

/**
 * Page-side hook: registers the search config (placeholder/label) with the
 * header search row and returns the current query plus its setter. When
 * `enabled` is false (e.g. members-only pages viewed anonymously) no bar shows.
 */
export function useDiscoverySearch({
  placeholder,
  ariaLabel,
  enabled = true,
}: DiscoverySearchConfig & { enabled?: boolean }) {
  const ctx = useContext(DiscoverySearchContext)
  const registerSearch = ctx?.registerSearch
  const unregisterSearch = ctx?.unregisterSearch

  useEffect(() => {
    if (!registerSearch || !unregisterSearch) return
    if (!enabled) {
      unregisterSearch()
      return
    }
    registerSearch({ placeholder, ariaLabel })
    return () => unregisterSearch()
  }, [registerSearch, unregisterSearch, enabled, placeholder, ariaLabel])

  return {
    query: enabled ? (ctx?.query ?? '') : '',
    setQuery: ctx?.setQuery ?? noop,
  }
}
